import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { NotePencil, Trash, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function InternalNotes({ contactId }) {
  const { t } = useI18n();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    if (!contactId) return;
    try {
      const { data } = await api.get(`/contacts/${contactId}/notes`);
      setNotes(data);
    } catch { /* silent */ }
  };

  useEffect(() => { setNotes([]); load(); }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const v = text.trim();
    if (!v) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/contacts/${contactId}/notes`, { contact_id: contactId, text: v });
      setNotes([data, ...notes]);
      setText("");
    } catch { toast.error("Error"); }
    finally { setPosting(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contacts/${contactId}/notes/${id}`);
      setNotes(notes.filter((n) => n.id !== id));
    } catch { toast.error("Error"); }
  };

  return (
    <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4" data-testid="internal-notes">
      <div className="flex items-center gap-2 mb-3">
        <NotePencil size={12} className="text-[#71717A]" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{t("inbox_notes")}</span>
        <span className="ml-auto flex items-center gap-1 text-[9px] text-[#71717A]">
          <LockKey size={10} /> team
        </span>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("inbox_notes_placeholder")}
        rows={2}
        className="bg-[#0A0A0C] border-[#27272A] text-xs resize-none"
        data-testid="internal-note-input"
      />
      <Button
        size="sm"
        onClick={add}
        disabled={posting || !text.trim()}
        className="w-full mt-2 bg-[#5E6AD2] hover:bg-[#4B55C3] h-8 text-xs disabled:opacity-40"
        data-testid="internal-note-add"
      >
        {posting ? "..." : t("inbox_notes_add")}
      </Button>
      <div className="mt-3 space-y-2">
        {notes.length === 0 && (
          <div className="text-[10px] text-[#71717A] italic text-center py-2">{t("inbox_notes_empty")}</div>
        )}
        {notes.map((n) => (
          <div key={n.id} className="group px-2.5 py-2 rounded-md bg-[#0A0A0C] border border-[#18181B]" data-testid={`internal-note-${n.id}`}>
            <div className="flex items-center gap-2 text-[10px] text-[#71717A] mb-1">
              <span className="text-white font-medium">{n.author_name}</span>
              <span className="ml-auto font-mono-jb">{relTime(n.created_at)}</span>
              <button onClick={() => remove(n.id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]" data-testid={`internal-note-delete-${n.id}`}>
                <Trash size={11} />
              </button>
            </div>
            <div className="text-xs text-[#A1A1AA] whitespace-pre-wrap break-words">{n.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
